import type {
  EScreenCompositionOperationKind,
  IDisplayItem,
  IDisplayItemProperty,
  IEvent,
  IScreenCompositionOperation,
  IScreenDesignDictionary
} from "@kuika/kuika-cl-model";
import type { CompositionOperation } from "./screen-composition-ledger";

/**
 * Turns the staged ledger into what Apply Screens sends.
 *
 * The ledger records ADDRESSES — "component 100 was placed under 30", "the `options` property of
 * 31 changed", "31's wiring changed". The values live in the tree the browser holds, and they are
 * read HERE, at send time, so the last edit before Apply is the one that travels and an
 * intermediate value never does.
 *
 * ## What each operation carries, and why not more
 *
 *   - **insert** — the whole subtree, because the server has no way to build a catalog component
 *     and duplicating the designer's construction rules there would be a second implementation of
 *     defaults, required children and stylings.
 *   - **configure** — the ONE named property, not the component. Sending the component would
 *     overwrite properties someone else changed on the server's copy meanwhile.
 *   - **wire** — the component's whole event list, because an action lives across events, actions,
 *     parameters and conditions; the event list is the smallest honest unit.
 *   - **delete** — nothing but the id.
 *
 * An operation whose target has since left the tree is DROPPED rather than sent: the server would
 * only refuse it, and refusing fails the whole step, which would strand every other operation the
 * user did on that screen.
 */

/**
 * The staged operation name → the number the wire carries.
 *
 * TYPE-ONLY dependency on the model, on purpose. The enum's four values are the whole contract,
 * and importing them as RUNTIME values ties this module to a build artifact: the model's sources
 * sit next to transpiled `.js` siblings, the resolver prefers a `.js` over the `.ts`, and a stale
 * sibling makes the enum arrive as `undefined` while the types still check out — which is exactly
 * how this broke once (an `export {}` sibling left behind by a merge). Mapping here keeps the
 * numbers next to the code that writes them, and `satisfies` still fails the build if the model's
 * enum stops accepting one.
 */
const OPERATION_KIND = {
  insert: 1,
  configure: 2,
  wire: 3,
  delete: 4,
  reorder: 5
} as const satisfies Record<CompositionOperation["op"], EScreenCompositionOperationKind>;

export interface IBuildCompositionPayloadResult {
  operations: IScreenCompositionOperation[];
  /**
   * Operations dropped because their target is no longer on the screen. Reported so the caller can
   * say something rather than silently sending less than the user staged.
   */
  droppedCount: number;
}

const readDisplayItem = (dictionary: IScreenDesignDictionary | undefined, kuikaId: number): IDisplayItem | undefined =>
  dictionary?.[kuikaId];

const findProperty = (di: IDisplayItem, propertyName: string): IDisplayItemProperty | undefined =>
  (di.item?.properties ?? []).find((property: IDisplayItemProperty): boolean => property.name === propertyName);

/**
 * Build the wire payload for one step's staged operations against the tree that holds them.
 *
 * Order is preserved: an insert of a component and a later configure of something inside it must
 * reach the server in the order the user did them.
 */
export function buildCompositionPayload(
  operations: readonly CompositionOperation[] | undefined,
  dictionary: IScreenDesignDictionary | undefined
): IBuildCompositionPayloadResult {
  const built: IScreenCompositionOperation[] = [];
  let droppedCount: number = 0;

  (operations ?? []).forEach((operation: CompositionOperation): void => {
    if (operation.op === "delete") {
      // The only operation that does NOT need the target to still be there — it is gone on
      // purpose, and the server has its own copy to remove it from.
      built.push({ kind: OPERATION_KIND.delete, kuikaId: operation.kuikaId });
      return;
    }

    const di: IDisplayItem | undefined = readDisplayItem(dictionary, operation.kuikaId);
    if (di === undefined) {
      droppedCount += 1;
      return;
    }

    if (operation.op === "insert") {
      built.push({
        kind: OPERATION_KIND.insert,
        kuikaId: operation.kuikaId,
        anchorKuikaId: operation.anchorKuikaId,
        subtree: di
      });
      return;
    }

    if (operation.op === "configure") {
      const property: IDisplayItemProperty | undefined = findProperty(di, operation.propertyName);
      if (property === undefined) {
        // The property was configured and then removed from the component (a type change can do
        // that). There is nothing to send, and asking the server to write `undefined` would be a
        // guess about what the user meant.
        droppedCount += 1;
        return;
      }
      built.push({
        kind: OPERATION_KIND.configure,
        kuikaId: operation.kuikaId,
        propertyName: operation.propertyName,
        property
      });
      return;
    }

    if (operation.op === "reorder") {
      // The FINAL order, read from the tree rather than accumulated from the moves that produced
      // it — see the ledger's `recordReorder`.
      built.push({
        kind: OPERATION_KIND.reorder,
        kuikaId: operation.kuikaId,
        childKuikaIds: (di.children ?? []).map((child: IDisplayItem): number => child.id)
      });
      return;
    }

    const events: IEvent[] = di.item?.events ?? [];
    built.push({
      kind: OPERATION_KIND.wire,
      kuikaId: operation.kuikaId,
      events
    });
  });

  return { operations: built, droppedCount };
}
